import { Callout, HTMLTable, Intent } from '@blueprintjs/core';
import classNames from 'classnames';
import * as React from 'react';

import {
  ProjectGeoDataByIri,
  ProjectGeoDataProperty,
} from 'src/constants/projectGeoData';
import { TEST_IDS } from 'src/constants/testIds';
import { AttributeTableItem } from 'src/containers/ProjectDiagram/components/attributesTable/types';
import { ProjectDiagramClassDrawerGeospatialInput } from 'src/containers/ProjectDiagram/components/classDrawer/ProjectDiagramClassDrawerGeospatialInput';
import { validateRangeForGeoProperty } from 'src/containers/ProjectDiagram/utils/geoData/validateRangeForGeoProperty';
import * as copy from 'src/templates/copy';
import { GEO_DATA_KEY_BY_IRI } from 'src/templates/queries/geoData';
import {
  DATATYPE_IRIS,
  MAP_IRIS,
  MAP_LATITUDE_IRI,
  MAP_LOCATION_IRI,
  MAP_LONGITUDE_IRI,
  MAP_NODE_SIZE_IRI,
  MAP_POPUP_CONTENT_IRI,
  MAP_POPUP_TITLE_IRI,
  MAP_TOOLTIP_CONTENT_IRI,
  MAP_TOOLTIP_TITLE_IRI,
} from 'src/templates/queries/iris';

type ProjectDiagramClassDrawerGeospatialTabProps = {
  attributeItems: AttributeTableItem[];
  geoDataByIri: ProjectGeoDataByIri;
  onUpdateGeoData: (geoDataByIri: ProjectGeoDataByIri) => void;
};

const INPUT_LABEL_BY_IRI: Record<string, string> = {
  [MAP_LOCATION_IRI]:
    copy.components.projectDiagram.classDrawer.geospatialTable.labels.location,
  [MAP_LATITUDE_IRI]:
    copy.components.projectDiagram.classDrawer.geospatialTable.labels.latitude,
  [MAP_LONGITUDE_IRI]:
    copy.components.projectDiagram.classDrawer.geospatialTable.labels.longitude,
  [MAP_NODE_SIZE_IRI]:
    copy.components.projectDiagram.classDrawer.geospatialTable.labels.nodeSize,
  [MAP_POPUP_TITLE_IRI]:
    copy.components.projectDiagram.classDrawer.geospatialTable.labels
      .popupTitle,
  [MAP_POPUP_CONTENT_IRI]:
    copy.components.projectDiagram.classDrawer.geospatialTable.labels
      .popupContent,
  [MAP_TOOLTIP_TITLE_IRI]:
    copy.components.projectDiagram.classDrawer.geospatialTable.labels
      .tooltipTitle,
  [MAP_TOOLTIP_CONTENT_IRI]:
    copy.components.projectDiagram.classDrawer.geospatialTable.labels
      .tooltipContent,
};

export const ProjectDiagramClassDrawerGeospatialTab: React.VFC<
  ProjectDiagramClassDrawerGeospatialTabProps
> = ({ attributeItems, geoDataByIri, onUpdateGeoData }) => {
  const propertyKeysWithInvalidSelectedAttributes = React.useMemo(() => {
    const propertyKeys = Object.values(GEO_DATA_KEY_BY_IRI).filter(
      (propertyKey) => {
        const geoProperty = geoDataByIri[propertyKey];
        if (!geoProperty || !geoProperty.isAttribute) {
          return false;
        }

        const foundItem = attributeItems.find(
          (item) => item.value === geoProperty.value
        );
        const isValid = foundItem?.ranges.every((range) =>
          validateRangeForGeoProperty(
            propertyKey,
            range.value as (typeof DATATYPE_IRIS)[number]
          )
        );
        return !isValid;
      }
    );
    return new Set(propertyKeys);
  }, [attributeItems, geoDataByIri]);

  const handleUpdateGeoPropertyForKey = React.useCallback(
    (
      propertyKey: keyof ProjectGeoDataByIri,
      property: ProjectGeoDataProperty | undefined
    ) => {
      const updatedGeoDataByIri: ProjectGeoDataByIri = {
        ...geoDataByIri,
        [propertyKey]: property || undefined,
      };
      onUpdateGeoData(updatedGeoDataByIri);
    },
    [geoDataByIri, onUpdateGeoData]
  );

  return (
    <div>
      <HTMLTable
        className={classNames(
          'sd-drawer-project-diagram-entity-table',
          'sd-drawer-project-diagram-entity-table--class-geospatial'
        )}
        data-testid={TEST_IDS.projectDiagram.entityDrawer.classGeospatialTable}
      >
        <thead>
          <tr>
            <th>
              {
                copy.components.projectDiagram.classDrawer.geospatialTable
                  .mapDetailColumn
              }
            </th>
            <th>
              {
                copy.components.projectDiagram.classDrawer.geospatialTable
                  .contentColumn
              }
            </th>
          </tr>
        </thead>

        <tbody>
          {MAP_IRIS.map((iri) => {
            const propertyKey = GEO_DATA_KEY_BY_IRI[iri];
            const hasInvalidSelectedAttribute =
              propertyKeysWithInvalidSelectedAttributes.has(propertyKey);
            return (
              <ProjectDiagramClassDrawerGeospatialInput
                key={iri}
                attributeItems={attributeItems}
                geoProperty={geoDataByIri[propertyKey] || undefined}
                geoPropertyKey={propertyKey}
                inputLabel={INPUT_LABEL_BY_IRI[iri]}
                onChangeGeoProperty={(property) => {
                  handleUpdateGeoPropertyForKey(propertyKey, property);
                }}
                showInvalidWarning={hasInvalidSelectedAttribute}
              />
            );
          })}
        </tbody>
      </HTMLTable>
      {propertyKeysWithInvalidSelectedAttributes.size ? (
        <Callout
          intent={Intent.WARNING}
          title={
            copy.components.projectDiagram.classDrawer.geospatialTable
              .invalidWarningCallout
          }
        />
      ) : null}
    </div>
  );
};
